import React, { useState } from "react";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, setDoc } from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

const RegisterForm = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // NEW: State for tracking hover on the login link
  const [isLinkHovered, setIsLinkHovered] = useState(false);

  // Orange theme colors
  const primaryOrange = "#FF6B00";
  const darkOrange = "#E66000";
  const lightGrayBorder = "#E5E7EB";

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await setDoc(
        doc(db, "users", user.uid),
        {
          name: user.displayName,
          email: user.email,
          role: "user",
          status: "in progress",
          createdAt: new Date(),
        },
        { merge: true },
      );

      alert(`Welcome ${user.displayName}`);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const countryOptions = [
    { value: "Sri Lanka", label: "Sri Lanka", flag: "https://flagcdn.com/lk.svg" },
    { value: "India", label: "India", flag: "https://flagcdn.com/in.svg" },
    { value: "USA", label: "USA", flag: "https://flagcdn.com/us.svg" },
    { value: "UK", label: "UK", flag: "https://flagcdn.com/gb.svg" },
    { value: "Canada", label: "Canada", flag: "https://flagcdn.com/ca.svg" },
    { value: "Australia", label: "Australia", flag: "https://flagcdn.com/au.svg" },
    { value: "UAE", label: "UAE", flag: "https://flagcdn.com/ae.svg" },
  ];

  const formatOptionLabel = ({ flag, label }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={flag}
        alt={label}
        style={{ width: "22px", height: "16px", marginRight: "10px", borderRadius: "2px" }}
      />
      <span style={{ fontSize: "14px", color: "#333" }}>{label}</span>
    </div>
  );

  const handleRegister = async () => {
    setError("");

    if (!name || !email || !country || !password || !agree) {
      setError("Please fill all fields and agree to terms.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateProfile(user, { displayName: name });

      await setDoc(doc(db, "users", user.uid), {
        name: name,
        email: email,
        country: country.value,
        role: "user",
        status: "in progress",
        createdAt: new Date(),
      });

      alert("Registration successful! Please login.");
      navigate("/login");
    } catch (err) {
      setError(err.code === "auth/email-already-in-use" ? "This email is already registered." : err.message);
    }
    setLoading(false);
  };

  const isButtonDisabled = loading || !name || !email || !country || !password || !agree;

  // --- STYLING ---
  const containerStyle = {
    maxWidth: "420px",
    margin: "40px auto",
    padding: "40px",
    borderRadius: "24px",
    backgroundColor: "white",
    boxShadow: "0 15px 35px rgba(255, 107, 0, 0.08)",
    textAlign: "center",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    border: `2px solid ${primaryOrange}`,
  };

  const inputStyle = {
    width: "100%",
    padding: "15px",
    margin: "10px 0",
    border: "none",
    borderBottom: `2px solid ${lightGrayBorder}`,
    outline: "none",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "border-color 0.3s",
    color: "#333",
    backgroundColor: "transparent"
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      borderBottom: `2px solid ${lightGrayBorder}`,
      borderRadius: 0,
      boxShadow: "none",
      margin: "10px 0",
      cursor: "pointer",
      minHeight: "45px",
      backgroundColor: "transparent",
      "&:hover": { borderColor: primaryOrange },
    }),
    placeholder: (base) => ({ 
        ...base, 
        fontSize: "14px", 
        color: "#9CA3AF",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        left: "10px"
    }),
    valueContainer: (base) => ({ ...base, padding: "0 5px" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  // --- BEAUTIFIED ICON ---
  const EyeIcon = ({ visible }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={primaryOrange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {visible ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </>
      )}
    </svg>
  );

  return (
    <div style={containerStyle}>
      <h2 style={{ color: "#1F2937", margin: "0 0 8px 0", fontSize: "28px", fontWeight: "800" }}>
        Create <span style={{color: primaryOrange}}>Account</span>
      </h2>
      <p style={{color: "#6B7280", margin: "0 0 30px 0", fontSize: "14px"}}>Join us and start your journey</p>

      <input type="text" placeholder="Domain Name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />

      <Select
        options={countryOptions}
        value={country}
        onChange={setCountry}
        placeholder="Select Country"
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
      />

      <div style={{ position: "relative" }}>
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{...inputStyle, paddingRight: "40px"}}
        />
        <span
          onClick={() => setShowPassword(!showPassword)}
          style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 10, display: "flex", alignItems: "center" }}
        >
          <EyeIcon visible={showPassword} />
        </span>
      </div>

      <div style={{ textAlign: "left", fontSize: "13px", color: "#4B5563", margin: "20px 0", display: "flex", alignItems: "center" }}>
        <input type="checkbox" id="terms" style={{ marginRight: "10px", accentColor: primaryOrange }} checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <label htmlFor="terms">I agree to the <span style={{color: primaryOrange, fontWeight: "600"}}>Terms & Conditions</span></label>
      </div>

      {error && <div style={{ color: "#EF4444", fontSize: "13px", backgroundColor: "#FEE2E2", padding: "10px", borderRadius: "8px", marginBottom: "15px" }}>{error}</div>}

      <button
        onClick={handleRegister}
        disabled={isButtonDisabled}
        style={{
          width: "100%",
          padding: "16px",
          background: isButtonDisabled
            ? "#E5E7EB"
            : `linear-gradient(135deg, ${primaryOrange} 0%, ${darkOrange} 100%)`,
          color: isButtonDisabled ? "#9CA3AF" : "white",
          border: "none",
          borderRadius: "12px",
          cursor: isButtonDisabled ? "not-allowed" : "pointer",
          fontWeight: "700",
          fontSize: "16px",
          marginTop: "10px",
          transition: "all 0.3s ease",
          boxShadow: isButtonDisabled ? "none" : "0 4px 12px rgba(255, 107, 0, 0.3)",
        }}
      >
        {loading ? "Creating..." : "REGISTER"}
      </button>

      <div style={{ display: "flex", alignItems: "center", margin: "25px 0", color: "#9CA3AF", fontSize: "13px" }}>
        <div style={{ flex: 1, height: "1px", backgroundColor: lightGrayBorder }} />
        <span style={{ margin: "0 10px", fontWeight: "500" }}>OR</span>
        <div style={{ flex: 1, height: "1px", backgroundColor: lightGrayBorder }} />
      </div>

      <div style={{ marginTop: "20px", fontSize: "14px", color: "#4B5563" }}>
        Already have an account?{" "}
        <Link 
            to="/login" 
            style={{ 
                color: primaryOrange, 
                fontWeight: "700", 
                textDecoration: isLinkHovered ? "underline" : "none", // Dynamic underline
                transition: "text-decoration 0.2s"
            }}
            // Added mouse events to track hover
            onMouseEnter={() => setIsLinkHovered(true)}
            onMouseLeave={() => setIsLinkHovered(false)}
        >
          Login Now
        </Link>
      </div>
    </div>
  );
};

export default RegisterForm;